import { Card, FormControl, List, ListItem, ListSubheader, Stack, Typography } from '@mui/joy';
import { Plus } from '@phosphor-icons/react';
import { lowerCase } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useUserPermissions } from 'auth/hooks';
import { Autocomplete, Button, Select } from 'core/components';
import { FormLabel, LabeledValue } from 'core/elements';
import { AccessPermissions, SortDirection } from 'core/enums';
import { IAutocompleteOption, IRequestPayload } from 'core/types';
import { QuoteEntityType, QuoteStatus } from 'quotes/enums';
import { quoteService } from 'quotes/services';
import { ICustomerQuote } from 'quotes/types';
import { Color, Direction, Level, Variant } from 'theme/enums';

interface IProps {
  entityType: QuoteEntityType;
  selectedQuote: ICustomerQuote | null;
  isCreatingQuote: boolean;
  onChangeEntityType: (entityType: QuoteEntityType) => void;
  onSelectQuote: (quote: ICustomerQuote | null) => void;
  onCreateQuote: () => void;
}

interface IQuoteOption extends ICustomerQuote {
  isRecentlyUsed: boolean;
}

const QUOTE_OPTIONS_PAGE_SIZE = 10;

// Lines can only be added to an order that has not been submitted yet - the same statuses the
// add-from-BoM flow offers.
const OPEN_ORDER_STATUSES = [
  QuoteStatus.ORDER_DRAFT_VENDOR,
  QuoteStatus.ORDER_REQUESTED_BY_CUSTOMER,
];

const optionDetailsOptions = {
  labelLevel: Level.BODY_SM,
  valueLevel: Level.BODY_SM,
  wrapperProps: { spacing: 0.5, direction: Direction.ROW },
};

// Where the picked reels go: a customer quote or order found by number, client or project, or a
// new one. With nothing typed the search lists the most recently updated ones first.
export const QuotePicker = ({
  entityType,
  selectedQuote,
  isCreatingQuote,
  onChangeEntityType,
  onSelectQuote,
  onCreateQuote,
}: IProps) => {
  const { t } = useTranslation();

  const { hasPermission } = useUserPermissions();

  const entityTypeOptions = useMemo(
    () => [
      {
        label: t('labelCustomerEntity', { entityType: QuoteEntityType.QUOTE }),
        value: QuoteEntityType.QUOTE,
      },
      ...(hasPermission(AccessPermissions.ACCESS_NEW_ORDER)
        ? [
            {
              label: t('labelCustomerEntity', { entityType: QuoteEntityType.ORDER }),
              value: QuoteEntityType.ORDER,
            },
          ]
        : []),
    ],
    [hasPermission, t]
  );

  const requestPayload: IRequestPayload = useMemo(
    () => ({
      filters:
        QuoteEntityType.ORDER === entityType
          ? { isOrder: 1, status: OPEN_ORDER_STATUSES.join('|') }
          : { isOrder: 0, archived: 0 },
      sortField: ['updated_at'],
      sortDir: [SortDirection.DESC],
    }),
    [entityType]
  );

  const getQuoteOptions = useCallback(async (payload: IRequestPayload, signal?: AbortSignal) => {
    const response = await quoteService.getCustomerQuotes(
      payload as Parameters<typeof quoteService.getCustomerQuotes>[0],
      signal
    );

    const isRecentlyUsed = !payload?.filters?.query;

    return {
      ...response,
      records: (response?.records ?? []).map((quote) => ({ ...quote, isRecentlyUsed })),
    };
  }, []);

  const renderQuoteOption = useCallback(
    (option: IAutocompleteOption) => {
      const quote = option as unknown as IQuoteOption;

      return (
        <Stack sx={{ gap: 0.25 }}>
          <Typography level={Level.TITLE_SM} color={Color.PRIMARY}>
            {quote?.id}
          </Typography>
          <LabeledValue
            label={t('labelCustomerCompanyName')}
            value={quote?.customerContactGroupName}
            options={optionDetailsOptions}
          />
          <LabeledValue
            label={t('labelJobName')}
            value={quote?.jobName}
            options={optionDetailsOptions}
          />
        </Stack>
      );
    },
    [t]
  );

  const renderGroup = useCallback(
    (params) => (
      <ListItem key={params.key} nested>
        {params.group && <ListSubheader sticky>{params.group}</ListSubheader>}
        <List>{params.children}</List>
      </ListItem>
    ),
    []
  );

  return (
    <Stack sx={{ gap: 2 }}>
      <Card
        variant={Variant.OUTLINED}
        color={Color.PRIMARY}
        sx={{ backgroundColor: 'neutral.100' }}
      >
        <Stack sx={{ gap: 2 }}>
          {/* A Select outside a form draws no label of its own, so it gets the one the form-bound
              Select draws. */}
          <FormControl>
            <FormLabel>{t('labelAddTo')}</FormLabel>
            <Select
              name="partFinderAddTo"
              value={entityType}
              options={entityTypeOptions}
              onChange={(_, value: QuoteEntityType) => value && onChangeEntityType(value)}
            />
          </FormControl>

          <Autocomplete
            key={entityType}
            name={`partFinderFindQuote${entityType}`}
            label={t('labelFindQuote', { entityType })}
            helperTexts={[t('messageFindQuoteHint', { entityType: lowerCase(entityType) })]}
            placeholder={t('labelSearch')}
            value={selectedQuote as unknown as IAutocompleteOption}
            getOptions={getQuoteOptions}
            requestPayload={requestPayload}
            includeFilterQuery
            pageSize={QUOTE_OPTIONS_PAGE_SIZE}
            getLabel={(option) => (option?.id ? String(option.id) : '')}
            renderOptions={renderQuoteOption}
            groupBy={(option) =>
              (option as unknown as IQuoteOption)?.isRecentlyUsed
                ? t('labelRecentlyUsedQuotes', { entityType })
                : ''
            }
            renderGroup={renderGroup}
            onChange={(_, option) => onSelectQuote(option as unknown as ICustomerQuote)}
          />

          <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 1 }}>
            <Typography level={Level.TITLE_MD}>{t('labelOr')}</Typography>
            <Button
              text={t('buttonLabelCreateNewCustomerQuote', { entityType })}
              variant={Variant.SOFT}
              startDecorator={<Plus />}
              onClick={onCreateQuote}
              disabled={isCreatingQuote}
            />
          </Stack>
        </Stack>
      </Card>

      {selectedQuote?.id && !isCreatingQuote && (
        <LabeledValue
          label={t('labelCustomer')}
          value={selectedQuote.customerContactGroupName}
          options={{
            labelLevel: Level.BODY_SM,
            valueLevel: Level.TITLE_MD,
            wrapperProps: { spacing: 0.5, direction: Direction.ROW, alignItems: 'center' },
          }}
        />
      )}
    </Stack>
  );
};
